// MIRRORS: panel/components/confirmDialog/confirmContext.tsx — identical copy; edit both, or delete this line on both to declare a deliberate split.
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { ConfirmDialog, type ConfirmOptions } from './confirmDialog';

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Holds the single app-wide dialog. Call sites stay one-liners:
 *   if (!(await confirm(tr.confirm.logout))) return;
 * which matters because the decision (is the form dirty?) and the action
 * (navigate) often live in different components.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (next) =>
      new Promise<boolean>((resolve) => {
        // A second confirm() while one is open would strand the first promise
        // forever; settle it as cancelled before taking over.
        resolveRef.current?.(false);
        resolveRef.current = resolve;
        setOptions(next);
      }),
    []
  );

  const settle = (ok: boolean) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setOptions(null);
    resolve?.(ok);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={options !== null}
        title={options?.title ?? ''}
        body={options?.body}
        confirmLabel={options?.confirmLabel ?? ''}
        cancelLabel={options?.cancelLabel ?? ''}
        // Forwarded explicitly like the rest. Note this list is a silent
        // failure mode: a new optional ConfirmOptions field type-checks
        // everywhere and simply never arrives unless it is added here.
        tone={options?.tone}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}
